import { first, get, isObject } from 'lodash';
import {
  referencedVariablesInExpression,
  replaceVariableCallsInExpression,
  replaceVariableInExpression,
} from '@gosupersimple/penguino';

import { exhaustiveCheck } from '@/lib/exhaustive';

import {
  getCell,
  isCellWithPipeline,
  isVariableCell,
  replaceCell,
  setCellPipeline,
  setVariableCellDefinition,
} from '@/core/cell';

import { setExplorationCells } from '@/core/exploration';

import {
  CompositeFilterCondition,
  DereferencedPipeline,
  ExplorationParameters,
  VariableDefinition,
  QueryVariables,
  Exploration,
  Pipeline,
  PipelineOperation,
  DereferencedPipelineOperation,
  ExplorationParameter,
  booleanParameter,
  dateParameter,
  stringParameter,
  dateRangeParameter,
  numberParameter,
  timeIntervalParameter,
  arrayParameter,
  EnumVariableDefinition,
  MultiselectVariableDefinition,
} from '../types';
import { isValueExpression } from '../pipeline/operation';
import { parseParameterValue } from '.';
import { variableColors } from '../components/variable-color';

export const isUniqueVariableKey = (key: string, existingKeys: string[]) =>
  !existingKeys.includes(key);

export const replaceVariableDefinition = (
  definition: VariableDefinition,
  exploration: Exploration,
  cellId: string,
) => {
  const cell = getCell(exploration.view.cells, cellId);
  if (cell === undefined) {
    throw new Error(`Could not find cell with id ${cellId}`);
  }
  if (!isVariableCell(cell)) {
    throw new Error(`Cell with id ${cellId} is not a variable cell`);
  }
  return setExplorationCells(
    exploration,
    replaceCell(exploration.view.cells, setVariableCellDefinition(cell, definition), cell.id),
  );
};

export const setVariableDefinitionDefaultValue = (
  definition: VariableDefinition,
  value: ExplorationParameter,
): VariableDefinition => {
  switch (definition.kind) {
    case 'string':
      return { ...definition, defaultValue: stringParameter.parse(value) };
    case 'number':
      return { ...definition, defaultValue: numberParameter.parse(value) };
    case 'enum':
      return { ...definition, defaultValue: stringParameter.parse(value) };
    case 'multiselect':
      return { ...definition, defaultValue: arrayParameter.parse(value) };
    case 'boolean':
      return { ...definition, defaultValue: booleanParameter.parse(value) };
    case 'date':
      return { ...definition, defaultValue: dateParameter.parse(value) };
    case 'date_range':
      return { ...definition, defaultRange: dateRangeParameter.parse(value).range };
    case 'time_interval':
      return { ...definition, defaultValue: timeIntervalParameter.parse(value) };
    default:
      return exhaustiveCheck(definition);
  }
};

export const getVariableDefinitions = (exploration: Exploration) =>
  exploration.view.cells.filter(isVariableCell).map((cell) => cell.definition);

export const getVariableExpression = (key: string) => `variable("${key}")`;

export const getVariableKeyFromExpression = (expression: string) =>
  first(referencedVariablesInExpression(expression));

export const getVariableColor = (
  key: string,
  variables: VariableDefinition[],
  colors = variableColors,
) => {
  const idx = variables.findIndex((variable) => variable.key === key);
  return colors[idx % colors.length];
};

export const getVariableDefaultOption = (variable: VariableDefinition): string | number => {
  switch (variable.kind) {
    case 'string':
      return '';
    case 'number':
      return 0;
    case 'enum':
    case 'multiselect':
      return variable.options.every(({ value }) => typeof value === 'number') ? 0 : '';
    default:
      return '';
  }
};

export const getQueryVariableTypeFromVariable = (variable: VariableDefinition) => {
  const staticType = VariableToQueryVariableTypeMap[variable.kind];
  if (staticType !== null) {
    return staticType;
  }
  // Fallback to dynamic type based on values
  if ('options' in variable && variable.queryOptions?.pipelineId === undefined) {
    return variable.options.every(({ value }) => typeof value === 'number') ? 'Number' : 'String';
  }
  return null;
};

export const VariableToQueryVariableTypeMap: Record<
  VariableDefinition['kind'],
  QueryVariables[number]['type'] | null
> = {
  string: 'String',
  number: 'Number',
  boolean: 'Boolean',
  enum: null,
  multiselect: 'Array',
  date: 'Date',
  date_range: 'String',
  time_interval: 'String',
} as const;

export const getDefaultValueForMultiTypeVariable = (
  definition: EnumVariableDefinition | MultiselectVariableDefinition,
) => {
  return getQueryVariableTypeFromVariable(definition) === 'Number' ? 0 : '';
};

const inferVariableTypeFromValue = (value: unknown): QueryVariables[number]['type'] => {
  switch (true) {
    case typeof value === 'string':
      return 'String';
    case typeof value === 'number':
      return 'Number';
    case typeof value === 'boolean':
      return 'Boolean';
    case Array.isArray(value):
      return 'Array';
    default:
      return 'String';
  }
};

/**
 * Zip together variable definitions and their values from parameters.
 */
export const getQueryVariablesFromParameters = (
  variables: VariableDefinition[],
  parameters: ExplorationParameters,
): QueryVariables =>
  variables
    .reduce<QueryVariables>((acc, variable) => {
      const { key } = variable;
      const value = get(parameters, key);
      const type = getQueryVariableTypeFromVariable(variable);
      return [
        ...acc,
        {
          type: type === null ? inferVariableTypeFromValue(value) : type,
          key,
          value: parseParameterValue(value, variable),
        } as QueryVariables[number],
      ];
    }, [])
    .sort((a, b) => a.key.localeCompare(b.key));

/**
 * Returns the variables of an exploration with default values.
 */
export const getExplorationVariables = (exploration: Exploration): QueryVariables =>
  getQueryVariablesFromParameters(getVariableDefinitions(exploration), {});

const getVariablesInExpression = (expression: string): string[] => {
  try {
    return referencedVariablesInExpression(expression);
  } catch {
    return [];
  }
};

export function filterVariablesForPipeline(
  pipeline: Pipeline | DereferencedPipeline,
  variables: QueryVariables,
) {
  const keys = getVariableKeysInPipeline(pipeline, variables);
  return variables.filter((v) => keys.includes(v.key));
}

export function getVariableKeysInPipeline(
  pipeline: Pipeline | DereferencedPipeline,
  variables: { key: string }[],
): string[] {
  const keys = variables.map((variable) => variable.key);

  const variableKeysInPipeline = pipeline.operations
    .map((operation) => {
      if (operation.disabled === true) {
        return [];
      }

      const keys: string[] = [];

      visitPipelineOperationExpressions(operation, (expression) => {
        keys.push(...getVariablesInExpression(expression));
        return expression;
      });

      return keys;
    })
    .flat();

  return variableKeysInPipeline.filter((key) => keys.includes(key));
}

function visitCompositeFilterExpressions(
  condition: CompositeFilterCondition,
  visitExpression: (expression: string) => string,
): CompositeFilterCondition {
  if ('value' in condition) {
    if (isObject(condition.value) && 'expression' in condition.value) {
      return {
        ...condition,
        value: {
          ...condition.value,
          expression: visitExpression(condition.value.expression),
        },
      };
    }
  }
  if ('operands' in condition) {
    return {
      ...condition,
      operands: condition.operands.map((operand) =>
        visitCompositeFilterExpressions(operand, visitExpression),
      ),
    };
  }
  return condition;
}

function visitPipelineOperationExpressions<
  T extends PipelineOperation | DereferencedPipelineOperation,
>(operation: T, visitExpression: (expression: string) => string): T {
  if (operation.operation === 'filter') {
    return {
      ...operation,
      parameters: visitCompositeFilterExpressions(operation.parameters, visitExpression),
    };
  }
  if (operation.operation === 'deriveField') {
    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        value: {
          ...operation.parameters.value,
          expression: visitExpression(operation.parameters.value.expression),
        },
      },
    };
  }

  if (operation.operation === 'cohort') {
    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        pipeline: visitPipelineExpressions(operation.parameters.pipeline, visitExpression),
      },
    };
  }

  if (operation.operation === 'groupAggregate') {
    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        aggregations: operation.parameters.aggregations.map((aggregation) => {
          if (aggregation.type === 'custom' && aggregation.value?.expression !== undefined) {
            return {
              ...aggregation,
              value: {
                ...aggregation.value,
                expression: visitExpression(aggregation.value.expression),
              },
            };
          }
          return aggregation;
        }),
        groups: operation.parameters.groups.map((group) => {
          if (isValueExpression(group.precision)) {
            return {
              ...group,
              precision: {
                ...group.precision,
                expression: visitExpression(group.precision.expression),
              },
            };
          }
          return group;
        }),
      },
    };
  }

  if (operation.operation === 'addRelatedColumn') {
    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        ...('pipeline' in operation.parameters && operation.parameters.pipeline !== undefined
          ? { pipeline: visitPipelineExpressions(operation.parameters.pipeline, visitExpression) }
          : {}),
      },
    };
  }

  if (operation.operation === 'relationAggregate') {
    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        aggregations: operation.parameters.aggregations.map((aggregation) => {
          if (aggregation.type === 'custom' && aggregation.value?.expression !== undefined) {
            return {
              ...aggregation,
              value: {
                ...aggregation.value,
                expression: visitExpression(aggregation.value.expression),
              },
            };
          }
          return aggregation;
        }),
        ...(operation.parameters.filter !== undefined
          ? {
              filter: visitCompositeFilterExpressions(operation.parameters.filter, visitExpression),
            }
          : {}),
        ...('pipeline' in operation.parameters && operation.parameters.pipeline !== undefined
          ? { pipeline: visitPipelineExpressions(operation.parameters.pipeline, visitExpression) }
          : {}),
      },
    };
  }

  if (operation.operation === 'joinPipeline') {
    if ('steps' in operation.parameters) {
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          steps: operation.parameters.steps.map((step) => ({
            ...step,
            pipeline: visitPipelineExpressions(step.pipeline, visitExpression),
          })),
        },
      };
    }

    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        pipeline: visitPipelineExpressions(operation.parameters.pipeline, visitExpression),
      },
    };
  }

  if (operation.operation === 'funnel') {
    return {
      ...operation,
      parameters: {
        ...operation.parameters,
        groups: operation.parameters.groups?.map((group) => {
          if (isValueExpression(group.precision)) {
            return {
              ...group,
              precision: {
                ...group.precision,
                expression: visitExpression(group.precision.expression),
              },
            };
          }
          return group;
        }),
        steps: operation.parameters.steps.map((step) => ({
          ...step,
          operations:
            'operations' in step && step.operations !== undefined
              ? step.operations.map((operation) =>
                  visitPipelineOperationExpressions(operation, visitExpression),
                )
              : [],
        })),
      },
    };
  }

  return operation;
}

const visitPipelineExpressions = <T extends Pipeline | DereferencedPipeline>(
  pipeline: T,
  visitExpression: (expression: string) => string,
): T => ({
  ...pipeline,
  operations: pipeline.operations.map((operation) =>
    visitPipelineOperationExpressions(operation, visitExpression),
  ),
});

export const replaceVariableKeys = (
  exploration: Exploration,
  previous: string,
  next: string,
): Exploration => ({
  ...exploration,
  view: {
    ...exploration.view,
    cells: exploration.view.cells.map((cell) =>
      isCellWithPipeline(cell)
        ? setCellPipeline(
            cell,
            visitPipelineExpressions(cell.pipeline, (expression) =>
              replaceVariableInExpression(expression, previous, next),
            ),
          )
        : cell,
    ),
  },
});

const variableToLiteral = (variable: QueryVariables[number]) => {
  switch (variable.type) {
    case 'Date':
      return `date("${variable.value}")`;
    default:
      return JSON.stringify(variable.value);
  }
};

export const dereferenceVariablesInPipeline = <T extends Pipeline | DereferencedPipeline>(
  pipeline: T,
  variables: QueryVariables,
): T => {
  return visitPipelineExpressions(pipeline, (expression) => {
    return variables.reduce(
      (expression, variable) =>
        replaceVariableCallsInExpression(expression, variable.key, variableToLiteral(variable)),
      expression,
    );
  });
};
